import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import JSZip from "jszip";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import AuthImage from "@/components/AuthImage";
import ModelPicker from "@/components/ModelPicker";
import BillingAlert from "@/components/BillingAlert";
import {
  Sparkles,
  Copy,
  Download,
  Check,
  Instagram,
  Loader2,
  Upload,
} from "lucide-react";

// Framing model, dari paling dekat sampai seluruh tubuh.
const FRAMINGS = [
  { id: "none", label: "Tanpa model", hint: "Fokus penuh ke produk" },
  { id: "holding_close", label: "Close-up pegang produk", hint: "Hanya wajah, bahu, tangan. Produk dipegang dekat wajah" },
  { id: "holding_half_body", label: "Setengah badan pegang produk", hint: "Waist up, produk di level dada menghadap kamera" },
  { id: "beside_medium", label: "Di samping produk", hint: "Orang dan produk seukuran dalam satu frame" },
  { id: "full_body_with_product", label: "Seluruh tubuh", hint: "Orang utuh sambil memegang produk" },
];

// Posisi model di brosur. Tanpa kendali ini jalur ini selalu memakai aturan
// roadway yang menambah orang kecil di sisi gambar -- tetap jalan, tapi tidak
// pernah jadi model yang memegang produk dari dekat.
function FramingPicker({ personFraming, setPersonFraming, setModel }) {
  const chip = 'border px-3 py-1.5 text-[12px] ';
  return (
    <>
      <h2 className='mt-5 font-mono text-[10px] uppercase tracking-[0.3em] text-zinc-500'>
        4 · Posisi model
      </h2>
      <div className='mt-2 flex flex-wrap gap-2'>
        {FRAMINGS.map((f) => (
          <button
            key={f.id}
            type='button'
            title={f.hint}
            aria-pressed={personFraming === f.id}
            onClick={() => {
              setPersonFraming(f.id);
              if (f.id !== 'none') setModel((m) => ({ ...m, enabled: true }));
            }}
            className={
              chip +
              (personFraming === f.id
                ? 'border-zinc-950 bg-zinc-950 text-[#FBFBF9]'
                : 'border-zinc-300 hover:border-zinc-900')
            }
          >
            {f.label}
          </button>
        ))}
      </div>
    </>
  );
}

const CATEGORIES = [
  { v: "", label: "Auto" },
  { v: "fashion", label: "Fashion" },
  { v: "minuman", label: "Minuman" },
  { v: "makanan", label: "Makanan" },
  { v: "kosmetik", label: "Kosmetik" },
  { v: "elektronik", label: "Elektronik" },
  { v: "aksesoris", label: "Aksesoris" },
  { v: "sepatu", label: "Sepatu" },
  { v: "furniture", label: "Furniture" },
];

const TONES = [
  { v: "minimal", label: "Minimalis" },
  { v: "premium", label: "Premium" },
  { v: "casual", label: "Casual" },
  { v: "hard_sell", label: "Hard Sell" },
  { v: "story", label: "Storytelling" },
];

const LANGUAGES = [
  { v: "id", label: "Bahasa Indonesia" },
  { v: "en", label: "English" },
];

const RATIOS = ["1:1", "4:5", "9:16", "16:9"];

// "brochure" menulis teks ke dalam gambar (nama produk + daftar manfaat), jadi
// hanya masuk akal kalau referensinya foto produk, bukan poster/infografis.
const MAX_CONTENT = 10;
const POLL_MS = 3000;

const STYLE_PRESETS = {
  umum: [
    "tone warna nude dan krem dengan pencahayaan studio lembut",
    "latar polos abu-abu muda dengan bayangan jatuh halus",
    "cahaya alami dari jendela, kesan hangat",
    "gaya premium dengan kontras rendah dan warna desaturasi",
    "pencahayaan dramatis dari satu sumber cahaya utama",
    "latar marmer putih bersih, hasil shoot komersial",
    "gaya editorial journaling dengan cahaya pagi lembut",
    "suasana hangat earth tone, tekstur kain terlihat jelas",
  ],
  fashion: [
    "full body editorial, model berpose natural, studio abu-abu",
    "gaya streetwear casual, model berjalan santai, dinding beton",
    "look minimal atasan polos, latar krem, cahaya lembut",
    "gaya luxury campaign, pose tegas, latar gelap",
    "detail kain close up dengan tekstur yang tajam",
    "gaya sporty casual, warna netral, pencahayaan terang",
    "nuansa earth tone, latar bata, pencahayaan sore",
    "gaya high fashion editorial, pose dinamis, kontras tegas",
  ],
  minuman: [
    "es batu terlihat dan uap tipis di atas gelas",
    "gelas dengan tetesan air, cahaya backlight, latar gelap",
    "siram dalam gelas kaca dengan tone warna segar",
    "botol dingin dengan kondensasi, latar meja kayu",
    "gaya menyegarkan, warna cerah, cahaya terang",
    "tumbler dengan sedotan, sudut 45 derajat, latar polos",
  ],
  makanan: [
    "warna emas karamel dengan uap hangat, latar kayu rustic",
    "flat lay dari atas dengan gaya iklan makanan",
    "sajian di piring keramik, cahaya alami, latar linen",
    "gaya food photography, tekstur makanan yang terlihat jelas",
  ],
  kosmetik: [
    "tekstur krim yang terlihat jelas dengan cahaya samping",
    "gaya skincare minimalis, warna netral, label terbaca",
    "tetesan air dan kesan embun, latar marmer",
    "gaya beauty campaign, warna peach, cahaya lembut",
  ],
  elektronik: [
    "permukaan metal reflektif dengan cahaya studio tiga titik",
    "gaya unboxing, sudut 45 derajat, detail produk tajam",
    "latar gelap dengan cahaya biru lembut, gaya futuristik",
    "gaya product shot minimal, latar abu-abu, bayangan tegas",
  ],
  aksesoris: [
    "detail logam berkilau dengan cahaya keras, latar beludru",
    "statement piece, warna bold, gaya editorial",
    "gaya daily simple, tangan memegang aksesori, natural light",
    "flat lay aksesoris dengan aksesori lain, tone netral",
  ],
  sepatu: [
    "sudut samping rendah, detail sol terlihat, latar kontras",
    "gaya streetwear dengan wearer berjalan, dinding kota",
    "gaya clean, sepatu di atas,kaca, cahaya studio",
    "mood cozy, sepatu dengan rajut, tone hangat",
  ],
  furniture: [
    "ruang terang dengan sudut 45 derajat, gaya Japandi",
    "detail tekstur kayu dengan cahaya pagi, warna netral",
    "gaya cozy, kain lembut, tanaman samping, tone hangat",
    "gaya modern, bentuk geometris, latar polos terang",
  ],
};

const STYLE_GROUP_LABELS = {
  umum: "Umum",
  fashion: "Fashion",
  minuman: "Minuman",
  makanan: "Makanan",
  kosmetik: "Kosmetik",
  elektronik: "Elektronik",
  aksesoris: "Aksesoris",
  sepatu: "Sepatu",
  furniture: "Furniture",
};


const STAGE_LABEL = {
  starting: "MenyiapkanΓÇª",
  planning: "Menyusun outline kontenΓÇª",
  rendering_slides: "Menggambar slideΓÇª",
  generating_images: "Membuat foto kontenΓÇª",
  writing_captions: "Menulis captionΓÇª",
  done: "Selesai",
  failed: "Gagal",
};

const AUDIENCES = [
  { v: "remaja", label: "Remaja" },
  { v: "ibu muda", label: "Ibu muda" },
  { v: "pasangan", label: "Pasangan" },
  { v: "profesional", label: "Profesional" },
  { v: "umum", label: "Umum" },
];

const GOALS = [
  { v: "edukasi", label: "Edukasi" },
  { v: "awareness", label: "Kesadaran" },
  { v: "informasi", label: "Informasi" },
  { v: "jualan", label: "Jualan" },
];

const VISUAL_STYLES = [
  { v: "minimal", label: "Minimalis" },
  { v: "bold typography", label: "Tipografi besar" },
  { v: "ilustrasi", label: "Ilustrasi" },
  { v: "editorial", label: "Editorial" },
  { v: "playful", label: "Ceria" },
];

function StyleChip({ text, active, onToggle }) {
  return (
    <button
      type="button"
      onClick={onToggle}
      className={`border px-3 py-1.5 text-left text-[12px] leading-snug ${
        active
          ? "border-zinc-950 bg-zinc-950 text-[#FBFBF9]"
          : "border-zinc-300 bg-white text-zinc-700 hover:border-zinc-900"
      }`}
    >
      {text}
    </button>
  );
}

export default function ContentStudio() {
  const { user } = useAuth();
  const inputRef = useRef(null);
  const pollRef = useRef(null);

  const [items, setItems] = useState([]);
  const [uploads, setUploads] = useState([]);
  const [reference, setReference] = useState("");
  const [count, setCount] = useState(3);
  const [category, setCategory] = useState("");
  const [topicOverride, setTopicOverride] = useState("");
  const [tone, setTone] = useState("minimal");
  const [language, setLanguage] = useState("id");
  const [aspectRatio, setAspectRatio] = useState("4:5");
  // Ketiga nilai ini masuk ke prompt apa adanya. Sengaja opsional: kalau
  // dikosongkan, model dilarang mengarang harga atau promo sendiri.
  const [priceLabel, setPriceLabel] = useState("");
  const [promoLabel, setPromoLabel] = useState("");
  const [ctaLabel, setCtaLabel] = useState("");
  const [styles, setStyles] = useState([]);
  const [aiSuggestions, setAiSuggestions] = useState([]);
  const [askingSuggestions, setAskingSuggestions] = useState(false);
  const [isiPrompt, setIsiPrompt] = useState("");
  const [mode, setMode] = useState("topic");
  const [topic, setTopic] = useState("");
  const [audience, setAudience] = useState("umum");
  const [goal, setGoal] = useState("edukasi");
  const [visualStyle, setVisualStyle] = useState("minimal");
  const [visibleItems, setVisibleItems] = useState(12);
  const [styleLock, setStyleLock] = useState(false);
  const [policyNote, setPolicyNote] = useState(null);
  // Saldo Google habis adalah satu-satunya error yang bisa diperbaiki pengguna
  // sendiri, jadi deserve panel penuh, bukan toast yang hilang sendiri.
  const [billingError, setBillingError] = useState(false);
  const [faceRefs, setFaceRefs] = useState([]);
  const [savedModels, setSavedModels] = useState([]);
  // Model opsional. enabled=false adalah default yang valid: tidak semua konten
  // perlu orang, dan memaksa model membuat pengguna memilih yang tidak dikehendaki.
  const [model, setModel] = useState({
    enabled: false,
    kind: "face",
    refId: "",
    savedId: "",
  });
  const [personFraming, setPersonFraming] = useState("none");
  const [personActivity, setPersonActivity] = useState("");

  useEffect(() => {
    api.get("/face-references").then((r) => setFaceRefs(r.data.items || [])).catch(() => {});
    api.get("/models").then((r) => setSavedModels(r.data.items || [])).catch(() => {});
  }, []);

  // Satu tempat untuk menempelkan model ke body request, supaya kedua jalur
  // (topic-pack dan content-pack) memakai nama field yang sama.
  const applyModel = (body) => {
    if (!model.enabled) return body;
    if (model.kind === "saved" && model.savedId) {
      body.saved_model_id = model.savedId;
    } else if (model.kind === "face" && model.refId) {
      body.face_reference_id = model.refId;
    }
    if (personFraming && personFraming !== "none") body.person_framing = personFraming;
    if (personActivity.trim()) body.person_activity = personActivity.trim();
    return body;
  };

  const toggleStyle = (text) =>
    setStyles((current) =>
      current.includes(text)
        ? current.filter((item) => item !== text)
        : [...current, text]
    );

  const composedDirection = () => {
    const parts = [];
    if (styles.length) parts.push(styles.join(". "));
    if (isiPrompt.trim()) parts.push(isiPrompt.trim());
    return parts.join(". ");
  };

  const askAiSuggestions = async () => {
    if (!reference) {
      toast.error("Pilih 1 foto referensi dulu.");
      return;
    }
    setAskingSuggestions(true);
    try {
      const body = {};
      if (reference.startsWith("up_")) body.upload_id = reference;
      else if (reference.startsWith("var_")) body.variant_id = reference;
      else body.job_id = reference;
      if (category) body.category = category;
      const { data } = await api.post("/instagram/prompt-suggestions", body);
      setAiSuggestions(data.items || []);
      if (!data.items?.length) toast.error("AI tidak memberi saran. Coba lagi.");
    } catch (error) {
      const detail = error.response?.data?.detail;
      toast.error(
        (typeof detail === "string" ? detail : detail?.message) ||
          "Gagal meminta saran AI."
      );
    } finally {
      setAskingSuggestions(false);
    }
  };

  const [uploading, setUploading] = useState(false);
  const [job, setJob] = useState(null);
  const [working, setWorking] = useState(false);
  const [busy, setBusy] = useState(false);
  const [copied, setCopied] = useState(null);
  const [pastPacks, setPastPacks] = useState([]);
  const [reopening, setReopening] = useState("");

  const LAST_PACK_KEY = "aps.lastContentPack";

  const restore = async (jobId) => {
    if (!jobId) return;
    setReopening(jobId);
    try {
      const { data } = await api.get(`/instagram/content-pack/${jobId}`);
      if (data.status === "completed" && data.slides?.length) {
        setJob(data);
        return;
      }
      window.localStorage.removeItem(LAST_PACK_KEY);
    } catch {
      window.localStorage.removeItem(LAST_PACK_KEY);
    } finally {
      setReopening("");
    }
  };

  useEffect(() => {
    const last = window.localStorage.getItem(LAST_PACK_KEY);
    if (last) restore(last);
  }, []);

  useEffect(() => {
    if (job?.status === "completed" && job.job_id) {
      window.localStorage.setItem(LAST_PACK_KEY, job.job_id);
    }
  }, [job]);

  useEffect(() => {
    api
      .get("/instagram/packs")
      .then((r) => setPastPacks(r.data.items || []))
      .catch(() => setPastPacks([]));
  }, [job?.status]);

  const reopen = async (jobId) => {
    setReopening(jobId);
    try {
      const { data } = await api.get(`/instagram/content-pack/${jobId}`);
      if (data.status !== "completed") {
        toast.error("Paket itu belum selesai.");
        return;
      }
      setJob(data);
      window.localStorage.setItem(LAST_PACK_KEY, jobId);
      window.scrollTo({ top: 0, behavior: "smooth" });
      toast.success("Paket dibuka ulang — tidak ada biaya baru.");
    } catch (error) {
      toast.error(
        error.response?.data?.detail?.message || "Gagal membuka paket."
      );
    } finally {
      setReopening("");
    }
  };

  useEffect(() => {
    api
      .get("/history?limit=60")
      .then((r) =>
        setItems(
          (r.data.items || []).filter((i) => i.status === "completed" && i.generated_path)
        )
      )
      .catch(() => setItems([]));
    return () => pollRef.current && clearTimeout(pollRef.current);
  }, []);

  useEffect(() => {
    if (reference) return;
    const first = uploads[0]?.upload_id || items[0]?.job_id || "";
    if (first) setReference(first);
  }, [items, uploads, reference]);

  const uploadFiles = async (fileList) => {
    const files = Array.from(fileList || []);
    if (!files.length) return;
    if (user?.license_activated === false) {
      toast.error("Activate your account with your code first.");
      return;
    }
    setUploading(true);
    try {
      const form = new FormData();
      files.forEach((file) => form.append("files", file));
      const { data } = await api.post("/instagram/sources", form);
      setUploads((current) => [...current, ...(data.items || [])]);
      if (data.items?.length) setReference(data.items[0].upload_id);
      toast.success(`${data.items.length} foto diunggah.`);
    } catch (error) {
      const detail = error.response?.data?.detail;
      toast.error(
        (typeof detail === "string" ? detail : detail?.message) || "Gagal mengunggah foto."
      );
    } finally {
      setUploading(false);
      if (inputRef.current) inputRef.current.value = "";
    }
  };

  const referencePath =
    uploads.find((u) => u.upload_id === reference)?.path ||
    items.find((i) => i.job_id === reference)?.generated_path ||
    "";

  const startTopic = async () => {
    if (topic.trim().length < 8) {
      toast.error("Tuliskan topik minimal 8 karakter.");
      return;
    }
    if (user?.license_activated === false) {
      toast.error("Activate your account with your code first.");
      return;
    }
    if (!user?.has_byok) {
      toast.error("Connect your Gemini API key in Profile first.");
      return;
    }
    setWorking(true);
    setPolicyNote(null);
    setBillingError(false);
    setJob({
      status: "running",
      stage: "planning",
      total: count,
      done_images: 0,
      slides: [],
    });
    try {
      const { data } = await api.post(
        "/instagram/topic-pack",
        applyModel({
          topic: topic.trim(),
          audience,
          goal,
          count,
          aspect_ratio: aspectRatio,
          language,
          style: visualStyle,
          visual_system: styleLock ? job?.visual_system || "" : null,
        }),
      );
      startPolling(data.job_id);
    } catch (error) {
      setWorking(false);
      setJob(null);
      const detail = error.response?.data?.detail;
      if (error.response?.status === 422) {
        setPolicyNote({
          message: detail?.message || "Topik ini belum bisa dibuat.",
          refocus: detail?.refocus || "",
        });
        return;
      }
      toast.error(
        (typeof detail === "string" ? detail : detail?.message) ||
          `Gagal membuat paket konten (${error.response?.status || "network"}).`
      );
    }
  };

  const startPolling = (jobId) => {
    pollRef.current = setInterval(async () => {
      try {
        const status = await api.get(`/instagram/content-pack/${jobId}`);
        setJob(status.data);
        if (status.data.status !== "running") {
          clearInterval(pollRef.current);
          pollRef.current = null;
          setWorking(false);
          if (status.data.status === "completed") {
            toast.success("Paket konten siap.");
          } else {
            const err = status.data.error;
            // Job yang sudah jalan bisa gagal di tengah karena saldo habis, jadi
            // panel top up harus muncul dari jalur polling juga, bukan hanya dari
            // catch saat permintaan awal.
            if (err?.code === "billing") setBillingError(true);
            else toast.error(err?.message || "Gagal membuat paket konten.");
          }
        }
      } catch (error) {
        clearInterval(pollRef.current);
        pollRef.current = null;
        setWorking(false);
        if (error.response?.data?.detail?.code === "billing") setBillingError(true);
        else
          toast.error(
            error.response?.data?.detail?.message || "Koneksi terputus saat membuat konten."
          );
      }
    }, POLL_MS);
  };

  const start = async () => {
    if (!reference) {
      toast.error("Pilih atau unggah 1 foto referensi dulu.");
      return;
    }
    if (user?.license_activated === false) {
      toast.error("Activate your account with your code first.");
      return;
    }
    if (!user?.has_byok) {
      toast.error("Connect your Gemini API key in Profile first.");
      return;
    }
    setWorking(true);
    setBillingError(false);
    setJob({
      status: "running",
      stage: "starting",
      total: count,
      done_images: 0,
      slides: [],
    });
    try {
      // Brosur hanya mungkin kalau referensinya foto produk, jadi pilihan
      // "Bentuk hasil" yang menentukan, bukan mode hasil di bawah. Mode hasil
      // hanya relevan untuk layout Foto produk. Kalau tetap digabung di sini,
      // memilih Brosur akan diam-diam ditimpa jadi Foto selama mode hasil
      // masih "Auto".
      const wantsBrochure = true;
      let body = {
        count,
        aspect_ratio: aspectRatio,
        tone,
        language,
        mode: "product",
        layout: wantsBrochure ? "brochure" : "photo",
      };
      if (wantsBrochure) {
        if (priceLabel.trim()) body.price_label = priceLabel.trim();
        if (promoLabel.trim()) body.promo_label = promoLabel.trim();
        if (ctaLabel.trim()) body.cta_label = ctaLabel.trim();
      }
      if (topicOverride.trim()) body.topic_override = topicOverride.trim();
      if (category) body.category = category;
      if (styleLock && job?.visual_system) body.visual_system = job.visual_system;
      const direction = composedDirection();
      if (direction) body.reference_prompt = direction;
      // Satu kolom, dua tujuan. Teksnya jadi poin yang tercetak di brosur
      // sekaligus arah tambahan di akhir prompt. Dikirim ke semua indeks
      // karena backend cuma memakai entri pada indeks gambar itu sendiri --
      // kalau hanya indeks 0, gambar kedua kembali ke poin dari kemasan.
      if (isiPrompt.trim()) {
        body.slide_texts = Array.from({ length: count }, () => isiPrompt.trim());
      }
      if (reference.startsWith("up_")) body.reference_upload_id = reference;
      else if (reference.startsWith("var_")) body.reference_variant_id = reference;
      else body.reference_job_id = reference;
      body = applyModel(body);
      const { data } = await api.post("/instagram/content-pack", body);
      if (data.mode === "content") {
        toast.success(
          `Referensi terdeteksi sebagai konten/poster. Sistem membuat ${count} slide baru bertopik: "${data.topic}".`
        );
      }
      startPolling(data.job_id);
    } catch (error) {
      setWorking(false);
      setJob(null);
      const detail = error.response?.data?.detail;
      let message =
        (typeof detail === "string" ? detail : detail?.message) ||
        "";
      if (!message) {
        if (error.code === "ECONNABORTED") {
          message = "Server took too long to respond. Try again in a moment.";
        } else if (!error.response) {
          message =
            "Could not reach the server. Check your internet connection, then try again.";
        } else {
          message = `Failed to build the content pack (${error.response.status}).`;
        }
      }
      toast.error(message);
    }
  };

  const copyText = async (text, tag) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(tag);
      setTimeout(() => setCopied(null), 1800);
    } catch {
      toast.error("Gagal menyalin");
    }
  };

  const downloadZip = async () => {
    if (!job?.slides?.length) return;
    setBusy(true);
    try {
      const zip = new JSZip();
      const base = job.filename_base || "instagram-pack";
      const folder = zip.folder(base);
      for (const [index, slide] of job.slides.entries()) {
        const response = await api.get(`/files/${slide.image_path}`, {
          responseType: "blob",
        });
        folder.file(`${String(index + 1).padStart(2, "0")}.png`, response.data);
        folder.file(
          `${String(index + 1).padStart(2, "0")}-caption.txt`,
          `${slide.caption}\n${slide.cta || ""}`.trim()
        );
      }
      folder.file(
        "00-post-caption.txt",
        `${job.post_caption}\n\n${(job.hashtags || []).join(" ")}\n\nAlt text:\n${job.slides
          .map((s, i) => `${i + 1}. ${s.alt_text}`)
          .join("\n")}\n\n${
          job.best_time ? `Best time: ${job.best_time}\n\n` : ""
        }Tips:\n${(job.posting_tips || []).map((t) => `- ${t}`).join("\n")}`
      );
      const blob = await zip.generateAsync({ type: "blob" });
      const url = URL.createObjectURL(blob);
      const anchor = document.createElement("a");
      anchor.href = url;
      anchor.download = `${base}.zip`;
      anchor.click();
      URL.revokeObjectURL(url);
      toast.success("ZIP diunduh — upload foto urut ke Instagram.");
    } catch {
      toast.error("Gagal membuat ZIP");
    } finally {
      setBusy(false);
    }
  };

  const fullCaption = job?.post_caption
    ? `${job.post_caption}\n\n${(job.hashtags || []).join(" ")}`
    : "";

  return (
    <div className="max-w-6xl mx-auto px-6 lg:px-10 py-12 lg:py-16">
      <p className="font-mono text-[11px] uppercase tracking-[0.3em] text-zinc-500">
        Content Studio
      </p>
      <h1 className="mt-3 font-serif text-4xl sm:text-5xl leading-none tracking-tight text-zinc-950">
        1 klik, paket konten jadi.
      </h1>
      <p className="mt-4 max-w-2xl text-[15px] leading-relaxed text-zinc-600">
        Tulis topiknya saja, atau pakai foto produk sebagai referensi. AI menyusun
        outline, menggambar tiap slide lengkap dengan teksnya, lalu menulis caption,
        hashtag, dan alt text — semuanya siap post ke Instagram.
      </p>

      {billingError && (
        <div className="mt-6 max-w-2xl">
          <BillingAlert onClose={() => setBillingError(false)} />
        </div>
      )}

      <div className="mt-8 flex flex-wrap gap-2">
        {[
          { v: "topic", label: "Dari topik", hint: "Tanpa foto referensi" },
          { v: "reference", label: "Dari foto referensi", hint: "Untuk produk" },
        ].map((tab) => (
          <button
            key={tab.v}
            type="button"
            onClick={() => setMode(tab.v)}
            className={`border px-4 py-2 text-left ${
              mode === tab.v
                ? "border-zinc-950 bg-zinc-950 text-[#FBFBF9]"
                : "border-zinc-300 bg-white hover:border-zinc-900"
            }`}
          >
            <span className="block text-[13px] font-medium">{tab.label}</span>
            <span className="block text-[11px] opacity-70">{tab.hint}</span>
          </button>
        ))}
      </div>

      {mode === "topic" && (
        <div className="mt-8 grid lg:grid-cols-12 gap-8">
          <div className="lg:col-span-7">
            <div className="border border-zinc-200 bg-white p-5">
              <h2 className="font-mono text-[10px] uppercase tracking-[0.3em] text-zinc-500">
                1 · Topik konten
              </h2>
              <p className="mt-2 text-[12px] leading-relaxed text-zinc-500">
                Tulis topik yang kamu inginkan. Sistem yang menentukan isi, judul di
                gambar, dan captionnya — kamu tidak perlu menulis prompt.
              </p>
              <textarea
                value={topic}
                onChange={(event) => setTopic(event.target.value)}
                rows={5}
                placeholder="mis. edukasi kesehatan seksual untuk ibu muda: kapan perlu ke dokter, dan tanda-tandanya"
                className="mt-3 w-full resize-none border border-zinc-300 px-3 py-3 text-[14px] leading-relaxed"
              />

              <h2 className="mt-6 font-mono text-[10px] uppercase tracking-[0.3em] text-zinc-500">
                2 · Audiens
              </h2>
              <div className="mt-2 flex flex-wrap gap-2">
                {AUDIENCES.map((a) => (
                  <button
                    key={a.v}
                    type="button"
                    onClick={() => setAudience(a.v)}
                    className={`border px-3 py-1.5 text-[12px] ${
                      audience === a.v
                        ? "border-zinc-950 bg-zinc-950 text-[#FBFBF9]"
                        : "border-zinc-300 hover:border-zinc-900"
                    }`}
                  >
                    {a.label}
                  </button>
                ))}
              </div>

              <h2 className="mt-6 font-mono text-[10px] uppercase tracking-[0.3em] text-zinc-500">
                3 · Tujuan
              </h2>
              <div className="mt-2 flex flex-wrap gap-2">
                {GOALS.map((g) => (
                  <button
                    key={g.v}
                    type="button"
                    onClick={() => setGoal(g.v)}
                    className={`border px-3 py-1.5 text-[12px] ${
                      goal === g.v
                        ? "border-zinc-950 bg-zinc-950 text-[#FBFBF9]"
                        : "border-zinc-300 hover:border-zinc-900"
                    }`}
                  >
                    {g.label}
                  </button>
                ))}
              </div>
            </div>
          </div>

          <div className="lg:col-span-5">
            <div className="border border-zinc-200 bg-white p-5">
              <h2 className="font-mono text-[10px] uppercase tracking-[0.3em] text-zinc-500">
                4 · Model di konten (opsional)
              </h2>
              <div className="mt-2">
                <ModelPicker
                  idPrefix="cs-topic"
                  faceRefs={faceRefs}
                  savedModels={savedModels}
                  value={model}
                  onChange={setModel}
                  activity={personActivity}
                  onActivityChange={setPersonActivity}
                  hint="Boleh dikosongkan. Kalau dipakai, model melakukan aktivitas di slide, jadi kontennya terasa demonstrasi bukan poster kosong."
                />
              </div>
            </div>
          </div>

          <div className="lg:col-span-5">
            <div className="border border-zinc-200 bg-white p-5">
              <h2 className="font-mono text-[10px] uppercase tracking-[0.3em] text-zinc-500">
                5 · Jumlah konten
              </h2>              <div className="mt-3 flex flex-wrap items-center gap-1.5">
                {Array.from({ length: MAX_CONTENT }, (_, i) => i + 1).map((n) => (
                  <button
                    key={n}
                    type="button"
                    onClick={() => setCount(n)}
                    className={`h-9 w-9 border font-mono text-[12px] ${
                      count === n
                        ? "border-zinc-950 bg-zinc-950 text-[#FBFBF9]"
                        : "border-zinc-300 hover:border-zinc-900"
                    }`}
                  >
                    {n}
                  </button>
                ))}
              </div>
              <p className="mt-2 text-[11px] text-zinc-400">
                {count === 1
                  ? "1 konten = 1 postingan tunggal (bukan carousel)."
                  : `${count} slide untuk 1 post carousel.`}
              </p>

              <h2 className="mt-5 font-mono text-[10px] uppercase tracking-[0.3em] text-zinc-500">
                5 · Gaya visual
              </h2>
              <div className="mt-2 flex flex-wrap gap-2">
                {VISUAL_STYLES.map((s) => (
                  <button
                    key={s.v}
                    type="button"
                    onClick={() => setVisualStyle(s.v)}
                    className={`border px-3 py-1.5 text-[12px] ${
                      visualStyle === s.v
                        ? "border-zinc-950 bg-zinc-950 text-[#FBFBF9]"
                        : "border-zinc-300 hover:border-zinc-900"
                    }`}
                  >
                    {s.label}
                  </button>
                ))}
              </div>

              <h2 className="mt-5 font-mono text-[10px] uppercase tracking-[0.3em] text-zinc-500">
                6 · Rasio & bahasa
              </h2>
              <div className="mt-2 flex flex-wrap gap-2">
                {RATIOS.map((r) => (
                  <button
                    key={r}
                    type="button"
                    onClick={() => setAspectRatio(r)}
                    className={`border px-3 py-1.5 font-mono text-[12px] ${
                      aspectRatio === r
                        ? "border-zinc-950 bg-zinc-950 text-[#FBFBF9]"
                        : "border-zinc-300 hover:border-zinc-900"
                    }`}
                  >
                    {r}
                  </button>
                ))}
                {LANGUAGES.map((l) => (
                  <button
                    key={l.v}
                    type="button"
                    onClick={() => setLanguage(l.v)}
                    className={`border px-3 py-1.5 text-[12px] ${
                      language === l.v
                        ? "border-zinc-950 bg-zinc-950 text-[#FBFBF9]"
                        : "border-zinc-300 hover:border-zinc-900"
                    }`}
                  >
                    {l.label}
                  </button>
                ))}
              </div>

              <button
                type="button"
                onClick={startTopic}
                disabled={working || topic.trim().length < 8}
                className="mt-5 flex w-full items-center justify-center gap-2 bg-zinc-950 px-5 py-3 text-sm text-[#FBFBF9] hover:bg-zinc-800 disabled:opacity-50"
              >
                {working ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" />
                    {STAGE_LABEL[job?.stage] || "Memproses…"}
                  </>
                ) : (
                  <>
                    <Instagram className="h-4 w-4" /> Buat paket konten
                  </>
                )}
              </button>
              {job?.visual_system && (
                <div className="mt-4 border border-zinc-200 bg-zinc-50 p-3">
                  <label className="flex items-start gap-2">
                    <input
                      type="checkbox"
                      checked={styleLock}
                      onChange={(event) => setStyleLock(event.target.checked)}
                      className="mt-0.5"
                    />
                    <span className="text-[12px] text-zinc-600">
                      Gunakan gaya visual paket terakhir (warna &amp; tipografi sama)
                    </span>
                  </label>
                  <p className="mt-2 border-l-2 border-zinc-300 pl-2 text-[11px] text-zinc-500">
                    {job.visual_system.slice(0, 220)}
                  </p>
                </div>
              )}
              {working && job && (
                <p className="mt-2 text-[11px] text-zinc-500">
                  {job.stage === "planning"
                    ? "AI sedang menyusun judul dan isi tiap slide…"
                    : job.stage === "rendering_slides"
                      ? `${job.done_images}/${job.total} slide selesai digambar. Jangan tutup halaman, biasanya 40–120 detik.`
                      : STAGE_LABEL[job.stage] || "Memproses…"}
                </p>
              )}

              <p className="mt-4 border-l-2 border-zinc-900 pl-3 text-[11px] text-zinc-500">
                Topik yang ditolak: konten seksual eksplisit, pornografi, seksualisasi anak,
                dan konten non-konsensual akan ditolak. Edukasi seks dan kesehatan
                reproduksi untuk orang dewasa tetap boleh.
              </p>
            </div>

            {policyNote && (
              <div className="mt-4 border border-amber-400 bg-amber-50 p-5">
                <p className="text-[13px] font-medium text-amber-900">
                  Topik belum bisa dibuat
                </p>
                <p className="mt-1 text-[12px] text-amber-800">{policyNote.message}</p>
                {policyNote.refocus && (
                  <button
                    type="button"
                    onClick={() => {
                      setTopic(policyNote.refocus);
                      setPolicyNote(null);
                    }}
                    className="mt-3 border border-amber-700 px-3 py-1.5 text-[12px] text-amber-900"
                  >
                    Pakai topik ini: {policyNote.refocus}
                  </button>
                )}
              </div>
            )}
          </div>
        </div>
      )}

      {mode === "reference" && (
      <div className="mt-10 grid lg:grid-cols-12 gap-8">

        <div className="lg:col-span-7">
          <div className="border border-zinc-200 bg-white p-5">
            <h2 className="font-mono text-[10px] uppercase tracking-[0.3em] text-zinc-500">
              1 · Foto referensi
            </h2>
            <p className="mt-2 text-[12px] leading-relaxed text-zinc-500">
              AI akan membuat foto baru dari foto ini: produk, warna, dan model tetap
              sama, tapi pose, angle, dan framing berbeda supaya feed-nya tidak terlihat
              sama.
            </p>

            <div className="mt-4 flex flex-wrap items-center gap-2">
              <button
                type="button"
                onClick={() => inputRef.current?.click()}
                disabled={uploading}
                className="inline-flex items-center gap-2 border border-zinc-300 px-4 py-2 text-[13px] hover:border-zinc-950 disabled:opacity-60"
              >
                <Upload className="h-4 w-4" />
                {uploading ? "Mengunggah…" : "Unggah foto"}
              </button>
              <span className="text-[11px] text-zinc-400">
                JPG/PNG/WEBP · maks 10 MB
              </span>
            </div>
            <input
              ref={inputRef}
              type="file"
              accept="image/jpeg,image/png,image/webp"
              multiple
              className="hidden"
              onChange={(event) => uploadFiles(event.target.files)}
            />

            {uploads.length > 0 && (
              <>
                <p className="mt-5 font-mono text-[10px] uppercase tracking-[0.3em] text-zinc-400">
                  Foto unggahan
                </p>
                <div className="mt-2 grid grid-cols-4 gap-3 sm:grid-cols-5">
                  {uploads.map((item) => (
                    <button
                      key={item.upload_id}
                      type="button"
                      title={item.filename}
                      onClick={() => setReference(item.upload_id)}
                      className={`relative aspect-[4/5] overflow-hidden border-2 ${
                        reference === item.upload_id
                          ? "border-zinc-950"
                          : "border-transparent"
                      }`}
                    >
                      <AuthImage width={320}
                        path={item.path}
                        alt={item.filename}
                        className="w-full h-full object-cover"
                      />
                      {reference === item.upload_id && (
                        <span className="absolute top-1.5 left-1.5 bg-zinc-950 px-1.5 py-0.5 font-mono text-[9px] uppercase tracking-[0.15em] text-white">
                          Referensi
                        </span>
                      )}
                    </button>
                  ))}
                </div>
              </>
            )}

            <p className="mt-5 font-mono text-[10px] uppercase tracking-[0.3em] text-zinc-400">
              Dari hasil generate
            </p>
            {items.length === 0 ? (
              <p className="mt-2 text-[12px] text-zinc-400">
                Belum ada foto hasil generate.{" "}
                <Link to="/create" className="underline">
                  Generate dulu di Photo Studio
                </Link>
                , atau unggah foto di atas.
              </p>
            ) : (
              <div className="mt-2 grid grid-cols-4 gap-3 sm:grid-cols-5">
                {items.slice(0, visibleItems).map((item) => (
                  <button
                    key={item.job_id}
                    type="button"
                    onClick={() => setReference(item.job_id)}
                    className={`relative aspect-[4/5] overflow-hidden border-2 ${
                      reference === item.job_id
                        ? "border-zinc-950"
                        : "border-transparent"
                    }`}
                  >
                    <AuthImage width={320}
                      path={item.generated_path}
                      alt=""
                      className="w-full h-full object-cover"
                    />
                    {reference === item.job_id && (
                      <span className="absolute top-1.5 left-1.5 bg-zinc-950 px-1.5 py-0.5 font-mono text-[9px] uppercase tracking-[0.15em] text-white">
                        Referensi
                      </span>
                    )}
                  </button>
                ))}
              </div>
            )}
            {items.length > visibleItems && (
              <button
                type="button"
                onClick={() => setVisibleItems((value) => value + 12)}
                className="mt-3 w-full border border-zinc-300 py-2 text-[12px] hover:border-zinc-950"
              >
                Tampilkan {Math.min(12, items.length - visibleItems)} foto lagi (
                {items.length - visibleItems} tersisa)
              </button>
            )}
          </div>
        </div>

        <div className="lg:col-span-5">
          <div className="border border-zinc-200 bg-white p-5">
            <h2 className="font-mono text-[10px] uppercase tracking-[0.3em] text-zinc-500">
              2 · Jumlah konten
            </h2>
            <div className="mt-3 flex flex-wrap items-center gap-1.5">
              {Array.from({ length: MAX_CONTENT }, (_, i) => i + 1).map((n) => (
                <button
                  key={n}
                  type="button"
                  onClick={() => setCount(n)}
                  className={`h-9 w-9 border font-mono text-[12px] ${
                    count === n
                      ? "border-zinc-950 bg-zinc-950 text-[#FBFBF9]"
                      : "border-zinc-300 hover:border-zinc-900"
                  }`}
                >
                  {n}
                </button>
              ))}
            </div>
            <p className="mt-2 text-[11px] text-zinc-400">
              {count} konten = {count} foto baru + {count} caption, untuk 1 post carousel.
            </p>

            <h2 className="mt-5 font-mono text-[10px] uppercase tracking-[0.3em] text-zinc-500">
              3 · Kategori
            </h2>
            <div className="mt-2 flex flex-wrap gap-2">
              {CATEGORIES.map((c) => (
                <button
                  key={c.v || "auto"}
                  type="button"
                  onClick={() => setCategory(c.v)}
                  className={`border px-3 py-1.5 text-[12px] ${
                    category === c.v
                      ? "border-zinc-950 bg-zinc-950 text-[#FBFBF9]"
                      : "border-zinc-300 hover:border-zinc-900"
                  }`}
                >
                  {c.label}
                </button>
              ))}
            </div>

            <FramingPicker
              personFraming={personFraming}
              setPersonFraming={setPersonFraming}
              setModel={setModel}
            />

            <h2 className="mt-5 font-mono text-[10px] uppercase tracking-[0.3em] text-zinc-500">
              5 · Gaya tulisan
            </h2>
            <div className="mt-2 flex flex-wrap gap-2">
              {TONES.map((t) => (
                <button
                  key={t.v}
                  type="button"
                  onClick={() => setTone(t.v)}
                  className={`border px-3 py-1.5 text-[12px] ${
                    tone === t.v
                      ? "border-zinc-950 bg-zinc-950 text-[#FBFBF9]"
                      : "border-zinc-300 hover:border-zinc-900"
                  }`}
                >
                  {t.label}
                </button>
              ))}
              {LANGUAGES.map((l) => (
                <button
                  key={l.v}
                  type="button"
                  onClick={() => setLanguage(l.v)}
                  className={`border px-3 py-1.5 text-[12px] ${
                    language === l.v
                      ? "border-zinc-950 bg-zinc-950 text-[#FBFBF9]"
                      : "border-zinc-300 hover:border-zinc-900"
                  }`}
                >
                  {l.label}
                </button>
              ))}
            </div>

            <h2 className="mt-5 font-mono text-[10px] uppercase tracking-[0.3em] text-zinc-500">
              6 · Rasio foto
            </h2>
            <div className="mt-2 flex flex-wrap gap-2">
              {RATIOS.map((r) => (
                <button
                  key={r}
                  type="button"
                  onClick={() => setAspectRatio(r)}
                  className={`border px-3 py-1.5 font-mono text-[12px] ${
                    aspectRatio === r
                      ? "border-zinc-950 bg-zinc-950 text-[#FBFBF9]"
                      : "border-zinc-300 hover:border-zinc-900"
                  }`}
                >
                  {r}
                </button>
              ))}
            </div>

              <div className="mt-4 grid gap-3 border-t border-zinc-200 pt-4">
                <div>
                  <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-500">
                    Harga (opsional)
                  </p>
                  <input
                    value={priceLabel}
                    onChange={(event) => setPriceLabel(event.target.value)}
                    placeholder="mis. Rp99.000"
                    maxLength={40}
                    className="mt-1 w-full border border-zinc-300 px-3 py-2 text-[14px] outline-none focus:border-zinc-950"
                  />
                </div>
                <div>
                  <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-500">
                    Promo (opsional)
                  </p>
                  <input
                    value={promoLabel}
                    onChange={(event) => setPromoLabel(event.target.value)}
                    placeholder="mis. Diskon 20% untuk pembelian pertama"
                    maxLength={70}
                    className="mt-1 w-full border border-zinc-300 px-3 py-2 text-[14px] outline-none focus:border-zinc-950"
                  />
                </div>
                <div>
                  <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-500">
                    Teks tombol
                  </p>
                  <input
                    value={ctaLabel}
                    onChange={(event) => setCtaLabel(event.target.value)}
                    placeholder="Beli Sekarang"
                    maxLength={28}
                    className="mt-1 w-full border border-zinc-300 px-3 py-2 text-[14px] outline-none focus:border-zinc-950"
                  />
                </div>
                <p className="text-[11px] text-zinc-500">
                  Semua teks masuk apa adanya ke gambar. Kalau dikosongkan, AI
                  tidak boleh membuat harga, diskon, atau klaim sendiri.
                </p>
              </div>

            <div className="mt-5">
              <h2 className="font-mono text-[10px] uppercase tracking-[0.3em] text-zinc-500">
                7 · Model (opsional)
              </h2>
              <div className="mt-2">
                <ModelPicker
                  idPrefix="cs-product"
                  faceRefs={faceRefs}
                  savedModels={savedModels}
                  value={model}
                  onChange={setModel}
                  activity={personActivity}
                  onActivityChange={setPersonActivity}
                  hint="Boleh dikosongkan. Kalau dipakai, model berdiri di samping produk tanpa menutupi produk."
                />
              </div>
            </div>

            <div className="mt-5">
              <div className="flex items-center justify-between gap-2">
                <h2 className="font-mono text-[10px] uppercase tracking-[0.3em] text-zinc-500">
                  8 · Gaya hasil
                </h2>
                <button
                  type="button"
                  onClick={askAiSuggestions}
                  disabled={askingSuggestions || !reference}
                  className="inline-flex items-center gap-1.5 border border-zinc-300 px-3 py-1.5 text-[12px] hover:border-zinc-950 disabled:opacity-50"
                >
                  {askingSuggestions ? (
                    <>
                      <Loader2 className="h-3.5 w-3.5 animate-spin" /> Meminta saran…
                    </>
                  ) : (
                    <>
                      <Sparkles className="h-3.5 w-3.5" /> Minta saran AI
                    </>
                  )}
                </button>
              </div>
              <p className="mt-2 text-[11px] text-zinc-400">
                Tidak perlu nulis prompt. Pilih satu atau beberapa gaya di bawah, atau
                minta saran AI yang khusus untuk foto referensimu.
              </p>

              {aiSuggestions.length > 0 && (
                <div className="mt-3">
                  <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-zinc-400">
                    Saran AI
                  </p>
                  <div className="mt-2 flex flex-wrap gap-2">
                    {aiSuggestions.map((text) => (
                      <StyleChip
                        key={text}
                        text={text}
                        active={styles.includes(text)}
                        onToggle={() => toggleStyle(text)}
                      />
                    ))}
                  </div>
                </div>
              )}

              <div className="mt-3 flex flex-wrap gap-2">
                {(category ? [category, "umum"] : ["umum"])
                  .filter((key) => STYLE_PRESETS[key])
                  .map((key) => (
                    <div key={key} className="w-full">
                      <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-zinc-400">
                        {STYLE_GROUP_LABELS[key]}
                      </p>
                      <div className="mt-1.5 flex flex-wrap gap-2">
                        {STYLE_PRESETS[key].map((text) => (
                          <StyleChip
                            key={text}
                            text={text}
                            active={styles.includes(text)}
                            onToggle={() => toggleStyle(text)}
                          />
                        ))}
                      </div>
                    </div>
                  ))}
              </div>

              <div className="mt-4 border border-zinc-200 bg-zinc-50 p-3">
                <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-zinc-400">
                  Arah yang akan dipakai
                </p>
                <p className="mt-1.5 text-[12px] leading-relaxed text-zinc-600">
                  {styles.length
                    ? styles.join(". ")
                    : "Belum ada gaya dipilih — AI akan memakai gaya default dari kategorinya."}
                </p>
              </div>

              <div className="mt-4">
                <label className="block text-[12px] text-zinc-600">
                  Isi brosur (opsional)
                </label>
                <textarea
                  value={isiPrompt}
                  onChange={(event) => setIsiPrompt(event.target.value)}
                  rows={4}
                  placeholder={'Satu baris per poin. Contoh:\nBahan alami 100%\nBPOM MD 1234'}
                  className="mt-1 w-full resize-y border border-zinc-300 px-3 py-2 text-sm leading-relaxed"
                />
                <p className="mt-1.5 text-[11px] leading-relaxed text-zinc-500">
                  Kosongkan kalau mau sistem yang membaca sendiri dari kemasan.
                  Isimu dipakai untuk teks yang tercetak di brosur sekaligus
                  mengarahkan gayanya, jadi jangan menulis klaim yang tidak kamu
                  siapkan buktinya.
                </p>
              </div>
            </div>

            <button
              type="button"
              onClick={start}
              disabled={working || !reference}
              className="mt-5 flex w-full items-center justify-center gap-2 bg-zinc-950 px-5 py-3 text-sm text-[#FBFBF9] hover:bg-zinc-800 disabled:opacity-50"
            >
              {working ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" />
                  {STAGE_LABEL[job?.stage] || "Memproses…"}
                </>
              ) : (
                <>
                  <Instagram className="h-4 w-4" /> Buat paket konten
                </>
              )}
            </button>
            {working && job && (
              <div className="mt-3">
                <div className="h-[3px] w-full bg-zinc-100">
                  <div
                    className="h-full bg-zinc-950 transition-all"
                    style={{
                      width: `${
                        job.stage === "writing_captions"
                          ? 90
                          : Math.round((job.done_images / (job.total || 1)) * 70)
                      }%`,
                    }}
                  />
                </div>
                <p className="mt-2 text-[11px] text-zinc-500">
                  {job.stage === "writing_captions"
                    ? `${job.done_images}/${job.total} foto jadi — sedang menulis caption…`
                    : `${job.done_images}/${job.total} foto dibuat. Jangan tutup halaman, biasanya 20–70 detik.`}
                </p>
              </div>
            )}
            <div>
              <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-500">
                Topik untuk konten baru (opsional)
              </p>
              <input
                value={topicOverride}
                onChange={(event) => setTopicOverride(event.target.value)}
                placeholder="mis. pola asuh anak: disiplin positif, batas yang jelas, dan komunikasi empatik"
                className="mt-2 w-full border border-zinc-300 px-3 py-2 text-[13px]"
              />
              <p className="mt-1 text-[11px] text-zinc-500">
                Kalau dikosongkan, sistem membaca tema besar dari referensimu, lalu
                membuat subtopik yang berbeda dari poster itu.
              </p>
            </div>
            {!reference && (
              <p className="mt-2 text-[11px] text-zinc-400">
                Pilih 1 foto referensi dulu (klik thumbnailnya).
              </p>
            )}
          </div>
        </div>
      </div>
      )}

      {job?.status === "running" && (
        <div className="mt-10 border border-zinc-200 bg-white p-5">
          <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-zinc-500">
            Paket terakhir
          </p>
          <p className="mt-2 text-[13px] text-zinc-700">
            Memuat hasil terakhir…
          </p>
        </div>
      )}

      {job?.status === "completed" && job.slides?.length > 0 && (
        <div className="mt-14">
          <div className="flex flex-wrap items-baseline justify-between gap-3">
            <h2 className="font-serif text-2xl tracking-tight">
              Paket siap posting ({job.slides.length} konten)
            </h2>
            <button
              type="button"
              onClick={downloadZip}
              disabled={busy}
              className="inline-flex items-center gap-2 border border-zinc-300 px-4 py-2 text-[13px] hover:border-zinc-950 disabled:opacity-60"
            >
              <Download className="h-4 w-4" />
              {busy ? "Menyiapkan…" : "Download ZIP"}
            </button>
          </div>

          <div className="mt-5 grid lg:grid-cols-12 gap-8">
            <div className="lg:col-span-7">
              <ol className="space-y-3">
                {job.slides.map((slide) => (
                  <li key={slide.job_id} className="border border-zinc-200 bg-white p-3">
                    <div className="flex gap-3">
                      <div className="w-20 shrink-0">
                        <AuthImage width={320}
                          path={slide.image_path}
                          alt=""
                          className="aspect-[4/5] w-full object-cover"
                        />
                      </div>
                      <div className="flex-1">
                        <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-400">
                          Konten {slide.order} · {slide.pose_label}
                        </p>
                        <textarea
                          value={slide.caption}
                          onChange={(event) =>
                            setJob((current) => ({
                              ...current,
                              slides: current.slides.map((s) =>
                                s.job_id === slide.job_id
                                  ? { ...s, caption: event.target.value }
                                  : s
                              ),
                            }))
                          }
                          rows={3}
                          className="mt-1 w-full resize-none border border-zinc-200 p-2 text-[13px] leading-relaxed"
                        />
                        <div className="mt-2 flex gap-2">
                          <button
                            type="button"
                            onClick={() =>
                              copyText(slide.caption, `slide-${slide.order}`)
                            }
                            className="inline-flex items-center gap-1 border border-zinc-300 px-2 py-1 text-[11px] hover:border-zinc-950"
                          >
                            {copied === `slide-${slide.order}` ? (
                              <Check className="h-3 w-3" />
                            ) : (
                              <Copy className="h-3 w-3" />
                            )}
                            Copy caption
                          </button>
                          <button
                            type="button"
                            onClick={() =>
                              copyText(slide.alt_text, `alt-${slide.order}`)
                            }
                            className="inline-flex items-center gap-1 border border-zinc-300 px-2 py-1 text-[11px] hover:border-zinc-950"
                          >
                            Copy alt text
                          </button>
                        </div>
                      </div>
                    </div>
                  </li>
                ))}
              </ol>
            </div>

            <div className="lg:col-span-5">
              <div className="border border-zinc-200 bg-zinc-50 p-5">
                <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-zinc-500">
                  Caption utama — untuk 1 postingan carousel
                </p>
                <p className="mt-1 text-[11px] text-zinc-400">
                  {job.slides.length} konten = {job.slides.length} slide dalam satu
                  postingan. Tiap slide punya caption sendiri di daftar di samping.
                </p>
                <textarea
                  value={fullCaption}
                  onChange={(event) => {
                    const parts = event.target.value.split("\n\n");
                    setJob((current) => ({
                      ...current,
                      post_caption: parts[0] || "",
                      hashtags: (parts[1] || "").split(/\s+/).filter(Boolean),
                    }));
                  }}
                  rows={10}
                  className="mt-3 w-full resize-none border border-zinc-200 bg-white p-3 text-[13px] leading-relaxed"
                />
                <button
                  type="button"
                  onClick={() => copyText(fullCaption, "post")}
                  className="mt-2 inline-flex items-center gap-2 border border-zinc-300 bg-white px-3 py-2 text-[12px] hover:border-zinc-950"
                >
                  {copied === "post" ? (
                    <Check className="h-3.5 w-3.5" />
                  ) : (
                    <Copy className="h-3.5 w-3.5" />
                  )}
                  Copy semua
                </button>

                {job.best_time && (
                  <p className="mt-4 text-[12px] text-zinc-600">
                    <span className="font-mono uppercase tracking-[0.2em] text-zinc-400">
                      Waktu terbaik{" "}
                    </span>
                    <br />
                    {job.best_time}
                  </p>
                )}
                {job.posting_tips?.length > 0 && (
                  <div className="mt-4">
                    <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-zinc-500">
                      Tips
                    </p>
                    <ul className="mt-2 space-y-1.5 text-[12px] text-zinc-600">
                      {job.posting_tips.map((tip, index) => (
                        <li key={index}>• {tip}</li>
                      ))}
                    </ul>
                  </div>
                )}

                <p className="mt-5 border-l-2 border-zinc-900 pl-3 text-[12px] text-zinc-500">
                  Cara pakai: buat carousel di Instagram, upload foto sesuai urutan
                  01–{String(job.slides.length).padStart(2, "0")}, salin caption utama,
                  lalu tempel caption tiap konten ke masing-masing foto.
                </p>
              </div>
            </div>
          </div>
        </div>
      )}

      {job?.status === "failed" && (
        <div className="mt-10 border border-rose-300 bg-rose-50 p-5">
          <p className="text-[13px] font-medium text-rose-900">
            Gagal membuat paket konten
          </p>
          <p className="mt-1 text-[12px] text-rose-800">
            {job.error?.message || "Terjadi kesalahan. Coba lagi beberapa saat lagi."}
          </p>
        </div>
      )}

      {!job && pastPacks.length > 0 && (
        <div className="mt-12">
          <h2 className="font-mono text-[10px] uppercase tracking-[0.3em] text-zinc-500">
            Paket sebelumnya
          </h2>
          <p className="mt-2 text-[12px] text-zinc-500">
            Hasil yang sudah pernah dibuat. Buka lagi kapan saja tanpa biaya tambahan.
          </p>
          <div className="mt-3 grid grid-cols-2 gap-3 sm:grid-cols-4 lg:grid-cols-5">
            {pastPacks.map((pack) => (
              <button
                key={pack.pack_id}
                type="button"
                onClick={() => reopen(pack.content_job_id || pack.pack_id)}
                disabled={reopening === pack.pack_id}
                className="relative aspect-[4/5] overflow-hidden border border-zinc-200 bg-zinc-100 hover:border-zinc-950 disabled:opacity-60"
              >
                {pack.cover_path && (
                  <AuthImage width={320}
                    path={pack.cover_path}
                    alt=""
                    className="w-full h-full object-cover"
                  />
                )}
                <span className="absolute inset-x-0 bottom-0 flex items-center justify-between gap-2 bg-gradient-to-t from-black/75 to-transparent px-2 pb-2 pt-6">
                  <span className="text-[11px] text-white">{pack.slide_count} konten</span>
                  <span className="font-mono text-[9px] uppercase tracking-[0.15em] text-white/80">
                    {reopening === pack.pack_id ? "…" : "Buka"}
                  </span>
                </span>
              </button>
            ))}
          </div>
        </div>
      )}

      {!job && (
        <p className="mt-12 flex items-center gap-2 text-[13px] text-zinc-500">
          <Sparkles className="h-4 w-4" />
          Foto dan caption dibuat dengan Gemini API key milikmu sendiri, jadi tidak ada
          biaya dari sisi aplikasi.
        </p>
      )}
    </div>
  );
}
